"use client";

import { TOUR_IDS } from "@bcc/components/tour/tour-ids";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { toast } from "@bcc/hooks/use-toast";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { Vendor } from "@client-shared/providers";
import { InstallationResult, InstallationsContext } from "@client-shared/providers/installations";
import { ServicesContext } from "@client-shared/providers/services";
import { shopifyService, woocommerceService } from "@client-shared/sampleData/sampleServices";
import { Service } from "@shared/types";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CONNECT_SERVICE_STEP_TEST_IDS } from "./connect-service-step.utils";
import { ServiceTile } from "./service-tile";
import { ShopDomainModal } from "./shop-domain-modal";
import type { ShopifyInstallPayload } from "./shopify-install-form";

/** Supported service platform IDs */
type SupportedPlatform = "shopify" | "woocommerce";

/** Narrowed service type when we know id is supported */
type SupportedService = Service & { id: SupportedPlatform };

/** Fallback services when API hasn't loaded yet */
const fallbackServices: Record<SupportedPlatform, Service> = {
  shopify: shopifyService,
  woocommerce: woocommerceService,
};

const isSupportedPlatform = (id: string): id is SupportedPlatform => id === "shopify" || id === "woocommerce";

type InstallationMethods = {
  installShopify?: (args: ShopifyInstallPayload & { vendorKey: string }) => Promise<unknown>;
  installWooCommerce?: (args: { shop: string; serviceName: string; returnPath?: string }) => Promise<unknown>;
  fetch?: () => void;
};

type InstallSuccessResponse = { success: true; redirectUrl?: string };

const isSuccessResponse = (result: unknown): result is InstallSuccessResponse =>
  typeof result === "object" && result !== null && (result as Record<string, unknown>).success === true;

const isExternalUrl = (url: string | undefined): url is string => typeof url === "string" && /^https?:\/\//i.test(url);

async function installSelectedService(args: {
  service: SupportedService;
  formData: Record<string, string> | ShopifyInstallPayload;
  methods?: InstallationMethods;
}): Promise<InstallationResult | undefined> {
  const { service, formData, methods } = args;

  if (service.id === "shopify") {
    const shopifyInstaller = methods?.installShopify;
    if (!shopifyInstaller) return undefined;

    const payload = formData as ShopifyInstallPayload;

    try {
      const res = await shopifyInstaller({ ...payload, vendorKey: "" });
      if (!isSuccessResponse(res)) {
        return { success: false, error: new Error("shopify installation failed") };
      }
      // OAuth flow returns an external Shopify authorize URL — propagate it so the caller redirects there.
      if (isExternalUrl(res.redirectUrl)) {
        return { success: true, redirectUrl: res.redirectUrl };
      }
      return { success: true, redirectUrl: ROUTE_PATHS.ONBOARDING_STEP_2 };
    } catch (e) {
      return {
        success: false,
        error: e instanceof Error ? e : new Error(String(e)),
      };
    }
  }

  // woocommerce
  const wooInstaller = methods?.installWooCommerce;
  if (!wooInstaller) return undefined;

  try {
    const res = await wooInstaller({
      shop: formData.shop ?? "",
      serviceName: service.name ?? "WooCommerce",
      returnPath: ROUTE_PATHS.ONBOARDING_STEP_2,
    });

    if (!isSuccessResponse(res) || !res.redirectUrl) {
      return {
        success: false,
        error: new Error("woocommerce installation failed"),
      };
    }
    return {
      success: true,
      redirectUrl: res.redirectUrl,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

export function ConnectServiceStep() {
  const { t } = useTranslation();

  const [selectedService, setSelectedService] = useState<Service | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const [_installationsState, , installationMethodsRaw] = useContext(InstallationsContext);
  const [servicesState] = useContext(ServicesContext);
  const [, , vendorUtils] = Vendor.useVendorContext();

  const installationMethods = installationMethodsRaw as unknown as InstallationMethods | undefined;

  // Region-derived locale options come from the vendor provider so the derivation
  // (and its localStorage fallback) lives in one place rather than per-component.
  const languageOptions = useMemo(() => vendorUtils?.getLanguageOptions?.(), [vendorUtils]);

  const availableServices = useMemo<SupportedService[]>(() => {
    const loaded =
      servicesState.services?.size && servicesState.services.size > 0
        ? Array.from(servicesState.services.values()).filter(
            (s): s is SupportedService => s.serviceType === "remote" && isSupportedPlatform(s.id)
          )
        : [];

    if (loaded.length > 0) return loaded;

    // fallback: only supported platforms
    return [fallbackServices.shopify as SupportedService, fallbackServices.woocommerce as SupportedService];
  }, [servicesState.services]);

  const openServiceModal = useCallback((service: Service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  }, []);

  const tiles = useMemo(() => {
    return availableServices.map((service) => {
      const label = t(`onboarding.connectService.${service.id}.label`);
      const description = t(`onboarding.connectService.${service.id}.description`);

      const onClick = () => {
        const serviceFromStore = servicesState.services?.get(service.id);
        if (serviceFromStore === undefined) {
          toast({
            variant: "destructive",
            title: t("onboarding.connectService.serviceUnavailable", {
              serviceName: service.name,
            }),
          });
          return;
        }
        openServiceModal(serviceFromStore);
      };

      return {
        key: service.id,
        platform: service.id,
        label,
        description,
        onClick,
      };
    });
  }, [availableServices, openServiceModal, servicesState.services, t]);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedService(undefined);
  }, []);

  const handleInstallation = useCallback(
    async (formData: Record<string, string> | ShopifyInstallPayload) => {
      if (!selectedService) return;
      if (!isSupportedPlatform(selectedService.id)) {
        throw new Error(`Unsupported platform: ${selectedService.id}`);
      }

      const result = await installSelectedService({
        service: selectedService as SupportedService,
        formData,
        methods: installationMethods,
      });

      if (result?.success && result.redirectUrl) {
        setIsModalOpen(false);
        const redirectUrl = result.redirectUrl;
        const isExternalRedirect = /^https?:\/\//i.test(redirectUrl);

        if (isExternalRedirect) {
          window.location.assign(redirectUrl);
        } else {
          navigate(redirectUrl);
        }
        return;
      }

      if (result?.error) {
        throw result.error;
      }

      throw new Error("Installation failed. Missing redirect URL.");
    },
    [installationMethods, navigate, selectedService]
  );

  return (
    <div className="flex flex-col items-center gap-8" data-testid={CONNECT_SERVICE_STEP_TEST_IDS.container}>
      <Card className="w-full max-w-3xl border-border-subtle shadow-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold text-text-primary" data-testid={CONNECT_SERVICE_STEP_TEST_IDS.title}>
            {t("onboarding.connectService.title")}
          </CardTitle>
          <CardDescription className="text-base text-text-muted" data-testid={CONNECT_SERVICE_STEP_TEST_IDS.description}>
            {t("onboarding.connectService.description")}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-6 md:flex-row" data-tour-id={TOUR_IDS.onboarding.serviceTiles}>
          {tiles.map((tile) => (
            <ServiceTile key={tile.key} platform={tile.platform} label={tile.label} description={tile.description} onClick={tile.onClick} />
          ))}
        </CardContent>
      </Card>

      <ShopDomainModal
        service={selectedService}
        isOpen={isModalOpen}
        onClose={handleClose}
        onSubmit={handleInstallation}
        languageOptions={languageOptions}
      />
    </div>
  );
}
